import { logout } from "../slices/authSlice";
import { clearStaffPersonaSession } from "./staffPersonaSession";

/**
 * Log out and land on /login via a **full-page navigation**.
 *
 * Awaits the `logout` thunk (Firebase `signOut` + auth-cache / activity-timestamp
 * clear) so those settle before unload, then `window.location.assign("/login")`
 * triggers a real document load — re-fetching `index.html` so the post-logout
 * session always comes up on the latest deployed bundle. The previous
 * `dispatch(logout()); navigate("/login")` was a client-side SPA transition that
 * never re-fetched `index.html`, so a long-open tab kept running its stale
 * bundle across the logout→login cycle.
 *
 * Tolerates a rejected `logout` (e.g. a Firebase `signOut` error) — we still
 * hard-navigate, so a broken session can't strand the user on a stale bundle.
 *
 * @param {import("@reduxjs/toolkit").Dispatch} dispatch - the store dispatch.
 * @param {string} [infoCode] - optional `?info=` banner code shown on the login
 *   page (e.g. `"session_expired"` for an idle/session-loss logout). Omit for a
 *   plain user-initiated logout, which should land on /login with no banner.
 */
export async function performLogout(dispatch, infoCode) {
  try {
    await dispatch(logout()).unwrap();
  } catch {
    // logout thunk rejected — hard-navigate anyway.
  }
  // Belt and braces for the staff session, which the `logout.fulfilled`
  // reducer also clears. A rejected thunk never reaches that reducer, and the
  // boot after it sees the same uid as the one already in the owner marker,
  // so nothing later would drop the entry: a staff member would sign back in
  // still wearing the persona they were spot-checking. Every logout path in
  // the app calls this function, so clearing here covers all of them.
  clearStaffPersonaSession();
  window.location.assign(infoCode ? `/login?info=${encodeURIComponent(infoCode)}` : "/login");
}
